//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import GeoLocationModel from './geoLocation.model';

/**
 * @service GeoLocationService
 * @description Service for managing geolocation data retrieval operations.
 * Provides HTTP methods for fetching geographical location details (Village, Mandal, District, State, etc.)
 * based on postal codes (pincodes).
 * Extends base Service class with generic type GeoLocationModel.
 *
 * @feature 1. Fetch location details by pincode
 * @feature 2. Cancel ongoing HTTP requests to prevent memory leaks
 * @feature 3. Promise-based async operations for geolocation data
 *
 * @architecture
 * Extends base Service<GeoLocationModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 * The endpoint `/aqu/geolocation/pincode` accepts a POST request with a pincode payload.
 *
 * @service-endpoint /aqu/geolocation/pincode
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class GeoLocationService extends Service<GeoLocationModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient
   *
   * Initializes service by calling parent Service constructor with baseUrl
   * configuration and service endpoint path.
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/aqu/geolocation/pincode');
  }

  /**
   * @method getPincodeDetailsByPincode
   * @async
   * @description Fetch geographical location details for a specific pincode
   *
   * Makes HTTP POST request to backend endpoint /aqu/geolocation/pincode.
   * Sends the pincode in the request body to retrieve associated location data
   * such as Village, Mandal, District, and State.
   *
   * @param {string} pincode - The postal code to search for
   * @returns {Promise<Array<GeoLocationModel>>} Promise resolving to array of GeoLocationModel objects
   * @side-effect
   * - Creates HTTP POST request and stores subscription reference in ongoingRequests
   * - Subscription can be cancelled via cancelOnGoingHttpRequests()
   *
   * @throws Rejects promise with HttpErrorResponse if request fails
   * @example
   * const locations = await geoLocationService.getPincodeDetailsByPincode('500081');
   * console.log(locations); // Array of GeoLocationModel objects
   */
  public getPincodeDetailsByPincode(pincode: string) {
    return new Promise<Array<GeoLocationModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<GeoLocationModel>>(`${this.baseUrl}${this.serviceUrl}`, {pincode: pincode}, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks
   *
   * Iterates through ongoingRequests array and unsubscribes from each active
   * subscription. Clears the requests array after unsubscribing from all.
   *
   * @execution-flow
   * 1. Iterate through each subscription in ongoingRequests array
   * 2. Check if subscription has unsubscribe function
   * 3. Call unsubscribe() on each active subscription
   * 4. Clear ongoingRequests array
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests
   * - Clears ongoingRequests array
   *
   * @usage Called during component ngOnDestroy to cleanup subscriptions
   * @example
   * ngOnDestroy() {
   *   this.geoLocationService.cancelOnGoingHttpRequests();
   * }
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
